import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  useParentBridge,
  type LayoutMode,
} from '../context/ParentBridgeContext';
import { useChatSession } from '../context/ChatSessionContext';
import { useVoice } from '../context/VoiceContext';
import { useInstance } from '../context/InstanceContext';

// Phones shouldn't see the expand toggle — the panel already fills the
// parent viewport on mobile, so "expand" has no real effect. The
// bootstrap relays the host viewport (live, on resize) via the
// ParentBridge; iframe's own `window.innerWidth` is iframe width and
// can't distinguish phone-fullscreen from desktop-corner.
const PHONE_BREAKPOINT_PX = 768;

// Cycle order for the header toggle. Bootstrap clamps anything above the
// configured cap, so we can step through and trust the next tick will
// reflect the actually-applied mode via wg:layout_changed.
function nextLayoutMode(current: LayoutMode, max: LayoutMode): LayoutMode {
  if (max === 'native') return 'native';
  if (max === 'expanded') {
    return current === 'native' ? 'expanded' : 'native';
  }
  // max === 'maximized' — three-step cycle
  if (current === 'native') return 'expanded';
  if (current === 'expanded') return 'maximized';
  return 'native';
}

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { requestClose, requestLayout, layoutMode, layoutMax, hostViewport } =
    useParentBridge();
  const { agent } = useChatSession();
  const voice = useVoice();
  const { resolvedTheme } = useInstance();
  // Partner-pickable identity layout. Defaults to the new centered design
  // when the partner hasn't chosen — older instances still rendered the
  // linear-left strip until 2026-05; new instances start centered.
  const headerLayout = resolvedTheme?.headerLayout || 'centered-extended';
  // Live phone detection. `hostViewport` updates whenever the parent
  // window resizes (debounced in bootstrap). Falls back to false until
  // the first wg:host_viewport message lands — desktop being the safer
  // default for chrome rendering.
  const phoneScreen = hostViewport
    ? hostViewport.isMobile || hostViewport.width < PHONE_BREAKPOINT_PX
    : false;
  // "Live" = the orb is active. We surface even pre-connection so the
  // user sees instant feedback that voice is engaged across views.
  const live = voice.isListening || voice.isConnecting;

  // Single source of truth = the chat backend's agent identity (the same
  // object ChatView's persona pill uses). The instance.name (partner-set
  // internal label) MUST NOT appear here.
  const name = agent?.displayName || 'Assistant';
  const avatarUrl = agent?.avatarUrl;
  const initial = name.trim().charAt(0).toUpperCase();

  const actions = (
    <div className="wg-header__actions">
      {layoutMax !== 'native' && !phoneScreen && (
        <button
          className="wg-header__expand"
          type="button"
          aria-label={
            layoutMode === 'native'
              ? t('header.expand', 'Expand')
              : t('header.collapse', 'Collapse')
          }
          title={
            layoutMode === 'native'
              ? t('header.expand', 'Expand')
              : t('header.collapse', 'Collapse')
          }
          onClick={() => requestLayout(nextLayoutMode(layoutMode, layoutMax))}
        >
          {layoutMode === 'native' ? <ExpandIcon /> : <CollapseIcon />}
        </button>
      )}
      <button
        className="wg-header__close"
        type="button"
        aria-label={t('common.close')}
        onClick={requestClose}
      >
        <Cross />
      </button>
    </div>
  );

  if (headerLayout === 'centered-extended') {
    // iOS-Messages-style identity card: big avatar centered, glassy pill
    // with the name below, close/expand pinned top-right. The avatar gets
    // a slight negative margin-top so its top edge nudges above the
    // panel's top padding — gives the "focal portrait" feel the design
    // calls for without coordinating with the bootstrap iframe sizing.
    return (
      <header
        className="wg-header wg-header--centered"
        data-wg-layout="centered"
      >
        {actions}
        <div className="wg-header__centered-avatar" aria-hidden>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" />
          ) : (
            <span className="wg-header__initial">{initial}</span>
          )}
        </div>
        <div className="wg-header__centered-pill">
          <span className="wg-header__name wg-header__name--centered">
            {name}
            {live && (
              <span
                className="wg-header__live-dot"
                aria-label={t('header.voice_live')}
                title={t('header.voice_live')}
              />
            )}
          </span>
        </div>
      </header>
    );
  }

  // Linear-left (legacy / opt-in): single-row strip with small avatar +
  // name on the left, actions on the right.
  return (
    <header className="wg-header" data-wg-layout="linear">
      <div className="wg-header__brand">
        <span className="wg-header__avatar" aria-hidden>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" />
          ) : (
            <span className="wg-header__initial">{initial}</span>
          )}
        </span>
        <span className="wg-header__name">
          {name}
          {live && (
            <span
              className="wg-header__live-dot"
              aria-label={t('header.voice_live')}
              title={t('header.voice_live')}
            />
          )}
        </span>
      </div>
      {actions}
    </header>
  );
};

export default Header;

function Cross() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  );
}
